import React from 'react';
import { X } from 'lucide-react';

/**
 * DemoVideoModal Component
 * 
 * To add your own demo video:
 * 1. Record a demo video of your app
 * 2. Upload to YouTube, Vimeo, or Loom
 * 3. Replace the videoUrl below with your video URL
 * 
 * Supported formats:
 * - YouTube: https://www.youtube.com/embed/VIDEO_ID
 * - Vimeo: https://player.vimeo.com/video/VIDEO_ID
 * - Loom: https://www.loom.com/embed/VIDEO_ID
 */

const DemoVideoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  // YouTube demo video
  const videoUrl = "https://www.youtube.com/embed/OZmTj2CznUM?si=ZB4PC2Qvf6WjmEBi";

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-12 pb-12 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Always Visible */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 bg-white hover:bg-gray-100 p-2 rounded-full shadow-lg transition-colors border border-gray-200"
        >
          <X className="w-6 h-6 text-gray-700" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-orange-500 p-6 pr-16">
          <h2 className="text-2xl md:text-3xl font-bold text-white">How to Use VirtualFit</h2>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
          {videoUrl ? (
            // Video Player with CTA
            <div className="space-y-6">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  src={videoUrl}
                  title="VirtualFit Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              {/* CTA Button */}
              <div className="text-center pt-4">
                <a
                  href="https://virtualfit.work/try"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Got It! Let's Try It Now
                </a>
              </div>
            </div>
          ) : (
            // Step-by-step guide when no video is available
            <div className="space-y-8">
              <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-6 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Quick Start Guide</h3>
                <p className="text-gray-600">Follow these simple steps to try on clothes virtually:</p>
              </div>

              {/* Step 1 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Upload or Paste Clothing</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Click "Try Now" and either upload an image of clothing from your device, or paste a URL from any online store (Amazon, Zara, H&M, etc.). You can try on any clothing item you find online!
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Select Your Size & Fit</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Choose your size (XS to XXL), preferred fit style (tight, regular, loose, or oversized), and optionally add your height for more accurate results. Our AI will generate a realistic visualization matching your specifications.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Generate & Shop</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Click "Generate Virtual Tryon" and wait 30-60 seconds. You'll see a professional fashion photo showing how the clothes look! Save to your gallery, share with friends, or click "Shop Now" to purchase the item.
                  </p>
                </div>
              </div>

              {/* Pro Tips */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl">
                <h4 className="text-lg font-bold text-gray-800 mb-3">💡 Pro Tips</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Use clear, high-quality clothing images for best results</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Try different fit styles to see what looks best on you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Save multiple options to your gallery and compare them</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>Share with friends to get their opinion before buying</span>
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="text-center pt-4">
                <a
                  href="https://virtualfit.work/try"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Got It! Let's Try It Now
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoVideoModal;
